// src/game/ClipExporter.js
// Records the Phaser canvas as a video clip and supports save/share.
// Uses canvas.captureStream() + MediaRecorder API.

export class ClipExporter {
  constructor(canvas) {
    this._canvas = canvas;
    this._recorder = null;
    this._chunks = [];
    this._blob = null;
  }

  // ── Check if recording is supported on this device ─────────────────────
  static isSupported() {
    return !!(
      typeof MediaRecorder !== 'undefined' &&
      HTMLCanvasElement.prototype.captureStream
    );
  }

  // ── Start recording the canvas ─────────────────────────────────────────
  start(fps = 30) {
    if (!ClipExporter.isSupported()) {
      console.warn('ClipExporter: MediaRecorder not supported');
      return false;
    }

    this._chunks = [];
    this._blob = null;

    const stream = this._canvas.captureStream(fps);

    // Prefer MP4 (better mobile support), fall back to WebM
    const mimeTypes = [
      'video/mp4;codecs=avc1',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
    ];
    let mimeType = '';
    for (const mt of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mt)) { mimeType = mt; break; }
    }

    try {
      this._recorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
        videoBitsPerSecond: 2_500_000, // 2.5 Mbps — good quality for 540×960
      });
    } catch {
      this._recorder = new MediaRecorder(stream);
    }

    this._recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this._chunks.push(e.data);
    };

    this._recorder.start(100); // collect data every 100ms
    return true;
  }

  // ── Stop recording and produce a Blob ──────────────────────────────────
  stop() {
    return new Promise((resolve) => {
      if (!this._recorder || this._recorder.state === 'inactive') {
        resolve(null);
        return;
      }

      this._recorder.onstop = () => {
        const type = this._recorder.mimeType || 'video/webm';
        this._blob = new Blob(this._chunks, { type });
        this._chunks = [];
        resolve(this._blob);
      };

      this._recorder.stop();
    });
  }

  // ── Get the recorded blob ──────────────────────────────────────────────
  getBlob() {
    return this._blob;
  }

  // ── Download the clip as a file ────────────────────────────────────────
  download(filename = 'pazurumon-clip') {
    if (!this._blob) return;

    const ext = this._blob.type.includes('mp4') ? 'mp4' : 'webm';
    const url = URL.createObjectURL(this._blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Share via Web Share API (native share sheet) ───────────────────────
  async share(title = 'PAZURUMON', text = '') {
    if (!this._blob) return false;

    const ext = this._blob.type.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([this._blob], `pazurumon-clip.${ext}`, { type: this._blob.type });

    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title,
          text: text || 'Check out my PAZURUMON gameplay! 🎮',
          files: [file],
        });
        return true;
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('Share failed:', err);
        return false;
      }
    }

    // Fallback: download
    this.download();
    return false;
  }

  // ── Cleanup ────────────────────────────────────────────────────────────
  destroy() {
    if (this._recorder?.state === 'recording') {
      this._recorder.stop();
    }
    this._recorder = null;
    this._chunks = [];
    this._blob = null;
  }
}
